import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';

import { conciergeService, ConciergeFileType } from '../services/conciergeService';

import { useConciergeData, useUpdateConciergeSection } from '../hooks/useConcierge';
import {
  ConciergeData,
  ConciergeSection,
  CONCIERGE_SECTIONS,
  MenuItem,
  HighlightCategory,
  HighlightItem,
  PriceSection,
  EatDrinkShopItem,
  EatDrinkShopSecondaryItem,
  DonateItem,
} from '../types';

// ─── Sub-form components ────────────────────────────────────────────────────

interface SimpleTextFormProps {
  title: string;
  shortText: string;
  context: string;
  onChange: (field: 'title' | 'short_text_content' | 'context', value: string) => void;
  showContext?: boolean;
}

const SimpleTextForm: React.FC<SimpleTextFormProps> = ({ title, shortText, context, onChange, showContext = true }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <TextField label="Title" value={title} onChange={e => onChange('title', e.target.value)} fullWidth />
    <TextField
      label="Short Text Content"
      value={shortText}
      onChange={e => onChange('short_text_content', e.target.value)}
      fullWidth
      multiline
      rows={3}
    />
    {showContext && context && (
      <TextField
        label="Screen Context (read-only)"
        value={context}
        fullWidth
        multiline
        rows={5}
        InputProps={{ readOnly: true }}
        helperText="Auto-generated description of the robot screen layout"
      />
    )}
  </Box>
);

// ─── Image upload field ──────────────────────────────────────────────────────

interface ImageUploadFieldProps {
  label: string;
  value: string;
  onChange: (filename: string) => void;
  museumCode: string;
  fileType?: ConciergeFileType;
  helperText?: string;
  fullWidth?: boolean;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label,
  value,
  onChange,
  museumCode,
  fileType = 'image',
  helperText,
  fullWidth = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const accept = fileType === 'image' ? 'image/*' : fileType === 'video' ? 'video/*' : 'audio/*';

  // Fetch a permanent public preview URL whenever the filename value changes.
  // Debounced 400ms so rapid typing doesn't spam the API.
  const fetchPreview = useCallback(
    async (filename: string) => {
      if (!filename.trim()) {
        setPreviewUrl(null);
        return;
      }
      try {
        const url = await conciergeService.getConciergeFilePreviewUrl(museumCode, filename, fileType);
        setPreviewUrl(url);
      } catch {
        setPreviewUrl(null);
      }
    },
    [museumCode, fileType],
  );

  useEffect(() => {
    setPreviewUrl(null);
    if (!value) return;
    const timer = setTimeout(() => fetchPreview(value), 400);
    return () => clearTimeout(timer);
  }, [value, fetchPreview]);

  const handleFile = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    setProgress(0);
    try {
      const result = await conciergeService.uploadConciergeFile(museumCode, file, fileType, setProgress);
      onChange(result.file_name);
      setPreviewUrl(result.public_url); // permanent URL — set directly without a second fetch
    } catch (err: any) {
      setUploadError(err.message ?? 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <Box sx={{ flexGrow: fullWidth ? 1 : undefined }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          label={label}
          value={value}
          onChange={e => onChange(e.target.value)}
          size="small"
          fullWidth={fullWidth}
          helperText={helperText}
          error={!!uploadError}
        />
        <Tooltip title={`Upload ${fileType}`}>
          <span>
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              sx={{ mt: 0.5, flexShrink: 0 }}
            >
              {uploading ? <CircularProgress size={18} /> : <UploadIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
      </Box>

      {uploading && <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5, borderRadius: 1 }} />}

      {uploadError && (
        <Typography variant="caption" color="error">
          {uploadError}
        </Typography>
      )}

      {previewUrl && fileType === 'image' && (
        <Box
          sx={{
            mt: 1,
            display: 'inline-block',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            lineHeight: 0,
          }}
        >
          <img
            src={previewUrl}
            alt="Preview"
            style={{ height: 72, maxWidth: 160, objectFit: 'cover', display: 'block' }}
            onError={() => setPreviewUrl(null)}
          />
        </Box>
      )}

      {previewUrl && fileType === 'video' && (
        <Box sx={{ mt: 1 }}>
          <video
            src={previewUrl}
            style={{ height: 72, maxWidth: 160, borderRadius: 4 }}
            controls={false}
            muted
            onError={() => setPreviewUrl(null)}
          />
        </Box>
      )}

      {previewUrl && fileType === 'audio' && (
        <Box sx={{ mt: 1 }}>
          <audio src={previewUrl} controls style={{ height: 32, maxWidth: 240 }} onError={() => setPreviewUrl(null)} />
        </Box>
      )}
    </Box>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

const ConciergeEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { section } = useParams<{ section: string }>();
  const [searchParams] = useSearchParams();
  const museumCode = searchParams.get('museum') ?? '';

  const sectionKey = section as ConciergeSection;
  const sectionMeta = CONCIERGE_SECTIONS.find(s => s.key === sectionKey);

  const { data: conciergeData, loading, error } = useConciergeData(museumCode || undefined);
  const updateSection = useUpdateConciergeSection();

  const [formData, setFormData] = useState<ConciergeData[ConciergeSection] | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  useEffect(() => {
    if (conciergeData && sectionKey) {
      setFormData(conciergeData[sectionKey] as ConciergeData[ConciergeSection]);
    }
  }, [conciergeData, sectionKey]);

  const handleSave = async () => {
    if (!formData || !museumCode) return;
    try {
      await updateSection.mutate({ museumCode, section: sectionKey, data: formData });
      setSnackbarMsg('Section saved successfully');
      setSnackbarError(false);
      setSnackbarOpen(true);
    } catch {
      setSnackbarMsg('Failed to save section');
      setSnackbarError(true);
      setSnackbarOpen(true);
    }
  };

  const backUrl = `/content?tab=concierge`;

  if (!sectionMeta) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Unknown concierge section: {section}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backUrl)} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  if (!museumCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">No museum specified. Please navigate from the Concierge tab.</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backUrl)} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Tooltip title="Back to Concierge">
          <IconButton onClick={() => navigate(backUrl)}>
            <BackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h5">{sectionMeta.label}</Typography>
          <Typography variant="body2" color="text.secondary">
            {sectionMeta.description}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && formData && (
        <>
          {/* ── welcome_screen ── */}
          {sectionKey === 'welcome_screen' &&
            (() => {
              const d = formData as ConciergeData['welcome_screen'];
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Secondary Screen
                  </Typography>
                  <TextField
                    label="Title"
                    value={d.params_secondary.title}
                    onChange={e =>
                      setFormData({ ...d, params_secondary: { ...d.params_secondary, title: e.target.value } })
                    }
                    fullWidth
                  />
                  <TextField
                    label="Short Text Content"
                    value={d.params_secondary.short_text_content}
                    onChange={e =>
                      setFormData({
                        ...d,
                        params_secondary: { ...d.params_secondary, short_text_content: e.target.value },
                      })
                    }
                    fullWidth
                    multiline
                    rows={3}
                  />
                  <TextField
                    label="Screen Context (read-only)"
                    value={d.params_primary.context}
                    fullWidth
                    multiline
                    rows={6}
                    InputProps={{ readOnly: true }}
                    helperText="Auto-generated description of the robot screen layout"
                  />
                </Box>
              );
            })()}

          {/* ── wayfinding ── */}
          {sectionKey === 'wayfinding' &&
            (() => {
              const d = formData as ConciergeData['wayfinding'];
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Section Colours & Labels"
                    value={d.params_secondary.short_text_content}
                    onChange={e =>
                      setFormData({ params_secondary: { ...d.params_secondary, short_text_content: e.target.value } })
                    }
                    fullWidth
                    multiline
                    rows={4}
                    helperText="Semicolon-separated list of: 0xFFCOLOR;Section Name;0xFFCOLOR;Section Name;..."
                  />
                  <TextField
                    label="Screen Context (read-only)"
                    value={d.params_secondary.context}
                    fullWidth
                    multiline
                    rows={6}
                    InputProps={{ readOnly: true }}
                    helperText="Auto-generated description of the robot screen layout"
                  />
                </Box>
              );
            })()}

          {/* ── video_call, robot_is_moving, robot_arrived, robot_going_home, robot_going_to_starting_point ── */}
          {[
            'video_call',
            'robot_is_moving',
            'robot_arrived',
            'robot_going_home',
            'robot_going_to_starting_point',
          ].includes(sectionKey) &&
            (() => {
              const d = formData as ConciergeData['video_call'];
              return (
                <SimpleTextForm
                  title={d.params_secondary.title}
                  shortText={d.params_secondary.short_text_content}
                  context={d.params_secondary.context ?? ''}
                  onChange={(field, value) =>
                    setFormData({ params_secondary: { ...d.params_secondary, [field]: value } })
                  }
                />
              );
            })()}

          {/* ── menu ── */}
          {sectionKey === 'menu' &&
            (() => {
              const d = formData as ConciergeData['menu'];
              const items = d.params_secondary;

              const updateItem = (index: number, field: keyof MenuItem, value: string) => {
                const updated = items.map((item, i) => {
                  if (i !== index) return item;
                  if (field === 'image') return item; // image handled separately
                  return { ...item, [field]: value };
                });
                setFormData({ ...d, params_secondary: updated });
              };

              const addItem = () => {
                setFormData({
                  ...d,
                  params_secondary: [...items, { title: '', sub_title: '', image: ['', ''] }],
                });
              };

              const removeItem = (index: number) => {
                setFormData({ ...d, params_secondary: items.filter((_, i) => i !== index) });
              };

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {items.map((item, i) => (
                    <Card key={i} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2">Menu Item {i + 1}</Typography>
                          <Tooltip title="Remove item">
                            <IconButton size="small" color="error" onClick={() => removeItem(i)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            label="Category Title"
                            value={item.title}
                            onChange={e => updateItem(i, 'title', e.target.value)}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Sub-title (displayed on button)"
                            value={item.sub_title}
                            onChange={e => updateItem(i, 'sub_title', e.target.value)}
                            fullWidth
                            size="small"
                          />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <ImageUploadField
                              label="Icon filename"
                              value={item.image[0]}
                              onChange={filename => {
                                const updated = items.map((it, idx) =>
                                  idx === i ? { ...it, image: [filename, it.image[1]] as [string, string] } : it,
                                );
                                setFormData({ ...d, params_secondary: updated });
                              }}
                              museumCode={museumCode}
                              helperText="e.g. ic_map.png"
                            />
                            <ImageUploadField
                              label="Background filename"
                              value={item.image[1]}
                              onChange={filename => {
                                const updated = items.map((it, idx) =>
                                  idx === i ? { ...it, image: [it.image[0], filename] as [string, string] } : it,
                                );
                                setFormData({ ...d, params_secondary: updated });
                              }}
                              museumCode={museumCode}
                              helperText="e.g. menu_highlights.jpg"
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  <Button startIcon={<AddIcon />} variant="outlined" onClick={addItem} sx={{ alignSelf: 'flex-start' }}>
                    Add Menu Item
                  </Button>
                </Box>
              );
            })()}

          {/* ── museum_highlights ── */}
          {sectionKey === 'museum_highlights' &&
            (() => {
              const d = formData as ConciergeData['museum_highlights'];

              const updateCategory = (index: number, value: string) => {
                const updated: HighlightCategory[] = (d.params_primary ?? []).map((c, i) =>
                  i === index ? { title: value } : c,
                );
                setFormData({ ...d, params_primary: updated });
              };

              const addCategory = () => {
                setFormData({ ...d, params_primary: [...(d.params_primary ?? []), { title: '' }] });
              };

              const removeCategory = (index: number) => {
                setFormData({ ...d, params_primary: (d.params_primary ?? []).filter((_, i) => i !== index) });
              };

              const updateHighlight = (index: number, field: keyof HighlightItem, value: string | number) => {
                const updated = d.params_secondary.map((item, i) => (i === index ? { ...item, [field]: value } : item));
                setFormData({ ...d, params_secondary: updated });
              };

              const addHighlight = () => {
                setFormData({
                  ...d,
                  params_secondary: [
                    ...d.params_secondary,
                    {
                      title: 'Museum Highlights',
                      sub_title: '',
                      short_text_content: '',
                      image: [''],
                      level: 1,
                      context: '',
                    },
                  ],
                });
              };

              const removeHighlight = (index: number) => {
                setFormData({ ...d, params_secondary: d.params_secondary.filter((_, i) => i !== index) });
              };

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Categories */}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                      Categories (Primary Screen Tabs)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {(d.params_primary ?? []).map((cat, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            label={`Category ${i + 1}`}
                            value={cat.title}
                            onChange={e => updateCategory(i, e.target.value)}
                            size="small"
                            sx={{ flexGrow: 1 }}
                          />
                          <IconButton size="small" color="error" onClick={() => removeCategory(i)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        size="small"
                        onClick={addCategory}
                        sx={{ alignSelf: 'flex-start', mt: 1 }}
                      >
                        Add Category
                      </Button>
                    </Box>
                  </Box>

                  <Divider />

                  {/* Highlight items */}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                      Highlight Items
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {d.params_secondary.map((item, i) => (
                        <Card key={i} variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2">
                                Item {i + 1}
                                {item.level !== undefined ? ` · Level ${item.level}` : ''}
                              </Typography>
                              <IconButton size="small" color="error" onClick={() => removeHighlight(i)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <TextField
                                label="Sub-title"
                                value={item.sub_title}
                                onChange={e => updateHighlight(i, 'sub_title', e.target.value)}
                                fullWidth
                                size="small"
                              />
                              <TextField
                                label="Short Text Content"
                                value={item.short_text_content}
                                onChange={e => updateHighlight(i, 'short_text_content', e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                              />
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <ImageUploadField
                                  label="Image filename"
                                  value={item.image[0] ?? ''}
                                  onChange={filename => {
                                    const updated = d.params_secondary.map((it, idx) =>
                                      idx === i ? { ...it, image: [filename] } : it,
                                    );
                                    setFormData({ ...d, params_secondary: updated });
                                  }}
                                  museumCode={museumCode}
                                  helperText="e.g. acm_highlights_01.png"
                                />
                                {item.level !== undefined && (
                                  <TextField
                                    label="Level"
                                    type="number"
                                    value={item.level ?? 1}
                                    onChange={e => updateHighlight(i, 'level', parseInt(e.target.value, 10) || 1)}
                                    size="small"
                                    sx={{ width: 100 }}
                                    inputProps={{ min: 1, max: 10 }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        size="small"
                        onClick={addHighlight}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Add Highlight Item
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })()}

          {/* ── admission_prices ── */}
          {sectionKey === 'admission_prices' &&
            (() => {
              const d = formData as NonNullable<ConciergeData['admission_prices']>;
              if (!d) return null;
              const sec = d.params_secondary;

              const updateSection = (index: number, field: keyof PriceSection, value: string) => {
                const updated = sec.prices.sections.map((s, i) => (i === index ? { ...s, [field]: value } : s));
                setFormData({ params_secondary: { ...sec, prices: { ...sec.prices, sections: updated } } });
              };

              const updateTierPrice = (sectionIdx: number, tierIdx: number, value: string) => {
                const updated = sec.prices.sections.map((s, i) => {
                  if (i !== sectionIdx) return s;
                  const tiers = s.pricing_tiers.map((t, j) => (j === tierIdx ? { ...t, price: value } : t));
                  return { ...s, pricing_tiers: tiers };
                });
                setFormData({ params_secondary: { ...sec, prices: { ...sec.prices, sections: updated } } });
              };

              const updateTierLabel = (sectionIdx: number, tierIdx: number, value: string) => {
                const updated = sec.prices.sections.map((s, i) => {
                  if (i !== sectionIdx) return s;
                  const tiers = s.pricing_tiers.map((t, j) => (j === tierIdx ? { ...t, label: value } : t));
                  return { ...s, pricing_tiers: tiers };
                });
                setFormData({ params_secondary: { ...sec, prices: { ...sec.prices, sections: updated } } });
              };

              const addPriceSection = () => {
                setFormData({
                  params_secondary: {
                    ...sec,
                    prices: {
                      ...sec.prices,
                      sections: [...sec.prices.sections, { title: '', pricing_tiers: [{ label: '', price: '' }] }],
                    },
                  },
                });
              };

              const removePriceSection = (index: number) => {
                setFormData({
                  params_secondary: {
                    ...sec,
                    prices: {
                      ...sec.prices,
                      sections: sec.prices.sections.filter((_, i) => i !== index),
                    },
                  },
                });
              };

              const addTier = (sectionIdx: number) => {
                const updated = sec.prices.sections.map((s, i) =>
                  i === sectionIdx ? { ...s, pricing_tiers: [...s.pricing_tiers, { label: '', price: '' }] } : s,
                );
                setFormData({ params_secondary: { ...sec, prices: { ...sec.prices, sections: updated } } });
              };

              const removeTier = (sectionIdx: number, tierIdx: number) => {
                const updated = sec.prices.sections.map((s, i) =>
                  i === sectionIdx ? { ...s, pricing_tiers: s.pricing_tiers.filter((_, j) => j !== tierIdx) } : s,
                );
                setFormData({ params_secondary: { ...sec, prices: { ...sec.prices, sections: updated } } });
              };

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    label="Page Title"
                    value={sec.title}
                    onChange={e => setFormData({ params_secondary: { ...sec, title: e.target.value } })}
                    fullWidth
                  />

                  {sec.prices.sections.map((ps, si) => (
                    <Card key={si} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography variant="subtitle2">Pricing Section {si + 1}</Typography>
                          <IconButton size="small" color="error" onClick={() => removePriceSection(si)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <TextField
                            label="Section Title"
                            value={ps.title}
                            onChange={e => updateSection(si, 'title', e.target.value)}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Sub-title (optional)"
                            value={ps.sub_title ?? ''}
                            onChange={e => updateSection(si, 'sub_title', e.target.value)}
                            fullWidth
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            Pricing Tiers
                          </Typography>
                          {ps.pricing_tiers.map((tier, ti) => (
                            <Box key={ti} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                label="Label"
                                value={tier.label ?? ''}
                                onChange={e => updateTierLabel(si, ti, e.target.value)}
                                size="small"
                                sx={{ flexGrow: 1 }}
                              />
                              <TextField
                                label="Price"
                                value={tier.price}
                                onChange={e => updateTierPrice(si, ti, e.target.value)}
                                size="small"
                                sx={{ width: 120 }}
                              />
                              <IconButton size="small" color="error" onClick={() => removeTier(si, ti)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addTier(si)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Add Tier
                          </Button>
                          <TextField
                            label="Disclaimer (optional)"
                            value={ps.disclaimer ?? ''}
                            onChange={e => updateSection(si, 'disclaimer', e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}

                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={addPriceSection}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Add Pricing Section
                  </Button>

                  <TextField
                    label="Footer Note (optional)"
                    value={sec.prices.footer_note ?? ''}
                    onChange={e =>
                      setFormData({
                        params_secondary: { ...sec, prices: { ...sec.prices, footer_note: e.target.value } },
                      })
                    }
                    fullWidth
                  />
                </Box>
              );
            })()}

          {/* ── getting_around ── */}
          {sectionKey === 'getting_around' &&
            (() => {
              const d = formData as ConciergeData['getting_around'];
              const updateAnswer = (index: number, value: string) => {
                const updated = d.answers.map((a, i) => (i === index ? value : a));
                setFormData({ ...d, answers: updated });
              };
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Prompt Question"
                    value={d.question}
                    onChange={e => setFormData({ ...d, question: e.target.value })}
                    fullWidth
                  />
                  <Typography variant="subtitle2" color="text.secondary">
                    Answer Options
                  </Typography>
                  {d.answers.map((answer, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        label={`Option ${i + 1}`}
                        value={answer}
                        onChange={e => updateAnswer(i, e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setFormData({ ...d, answers: d.answers.filter((_, j) => j !== i) })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => setFormData({ ...d, answers: [...d.answers, ''] })}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Add Option
                  </Button>
                </Box>
              );
            })()}

          {/* ── eat_drink_shop (ACM) ── */}
          {sectionKey === 'eat_drink_shop' &&
            (() => {
              const d = formData as NonNullable<ConciergeData['eat_drink_shop']>;
              if (!d) return null;

              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const setEds = (v: NonNullable<ConciergeData['eat_drink_shop']>) => setFormData(v as any);

              const updatePrimary = (index: number, field: keyof EatDrinkShopItem, value: string) => {
                const updated = d.params_primary.map((it, i) => (i === index ? { ...it, [field]: value } : it));
                setEds({ ...d, params_primary: updated });
              };

              const updateSecondary = (index: number, field: keyof EatDrinkShopSecondaryItem, value: string) => {
                const updated = d.params_secondary.map((it, i) => (i === index ? { ...it, [field]: value } : it));
                setEds({ ...d, params_secondary: updated });
              };

              const addItem = () => {
                setEds({
                  ...d,
                  params_primary: [...d.params_primary, { title: '', short_text_content: '', image: [''] }],
                  params_secondary: [...d.params_secondary, { title: 'ABOUT', short_text_content: '', image: [''] }],
                });
              };

              const removeItem = (index: number) => {
                setEds({
                  ...d,
                  params_primary: d.params_primary.filter((_, i) => i !== index),
                  params_secondary: d.params_secondary.filter((_, i) => i !== index),
                });
              };

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {d.params_primary.map((item, i) => (
                    <Card key={i} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography variant="subtitle2">Listing {i + 1}</Typography>
                          <IconButton size="small" color="error" onClick={() => removeItem(i)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <TextField
                            label="Name"
                            value={item.title}
                            onChange={e => updatePrimary(i, 'title', e.target.value)}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Opening Hours"
                            value={item.short_text_content}
                            onChange={e => updatePrimary(i, 'short_text_content', e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                            size="small"
                          />
                          <ImageUploadField
                            label="Image filename"
                            value={item.image[0] ?? ''}
                            onChange={filename => {
                              const updated = d.params_primary.map((it, idx) =>
                                idx === i ? { ...it, image: [filename] } : it,
                              );
                              setEds({ ...d, params_primary: updated });
                            }}
                            museumCode={museumCode}
                          />
                          <Divider />
                          <Typography variant="caption" color="text.secondary">
                            About
                          </Typography>
                          <TextField
                            label="About Description"
                            value={d.params_secondary[i]?.short_text_content ?? ''}
                            onChange={e => updateSecondary(i, 'short_text_content', e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                            size="small"
                          />
                          <ImageUploadField
                            label="About image filename"
                            value={d.params_secondary[i]?.image[0] ?? ''}
                            onChange={filename => {
                              const updated = d.params_secondary.map((it, idx) =>
                                idx === i ? { ...it, image: [filename] } : it,
                              );
                              setEds({ ...d, params_secondary: updated });
                            }}
                            museumCode={museumCode}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  <Button startIcon={<AddIcon />} variant="outlined" onClick={addItem} sx={{ alignSelf: 'flex-start' }}>
                    Add Listing
                  </Button>
                </Box>
              );
            })()}

          {/* ── donate (CCM) ── */}
          {sectionKey === 'donate' &&
            (() => {
              const d = formData as NonNullable<ConciergeData['donate']>;
              if (!d) return null;
              const items = d.params_secondary as DonateItem[];
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const setDonate = (v: NonNullable<ConciergeData['donate']>) => setFormData(v as any);

              const updateItem = (index: number, field: keyof DonateItem, value: string) => {
                const updated = items.map((it, i) => (i === index ? { ...it, [field]: value } : it));
                setDonate({ params_secondary: updated });
              };

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {items.map((item, i) => (
                    <Card key={i} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography variant="subtitle2">Donation Option {i + 1}</Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDonate({ params_secondary: items.filter((_, j) => j !== i) })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <TextField
                            label="Title"
                            value={item.title}
                            onChange={e => updateItem(i, 'title', e.target.value)}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Description"
                            value={item.short_text_content}
                            onChange={e => updateItem(i, 'short_text_content', e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                          />
                          <ImageUploadField
                            label="Image filename"
                            value={item.image[0] ?? ''}
                            onChange={filename => {
                              const updated = items.map((it, idx) => (idx === i ? { ...it, image: [filename] } : it));
                              setDonate({ params_secondary: updated });
                            }}
                            museumCode={museumCode}
                          />
                          {item.image_text !== undefined && (
                            <TextField
                              label="Image Caption"
                              value={item.image_text}
                              onChange={e => updateItem(i, 'image_text', e.target.value)}
                              fullWidth
                              size="small"
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={() =>
                      setDonate({ params_secondary: [...items, { title: '', short_text_content: '', image: [''] }] })
                    }
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Add Option
                  </Button>
                </Box>
              );
            })()}

          {/* ── contribute ── */}
          {sectionKey === 'contribute' &&
            (() => {
              const d = formData as ConciergeData['contribute'];
              const ps = d.params_secondary;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Title"
                    value={ps.title}
                    onChange={e => setFormData({ params_secondary: { ...ps, title: e.target.value } })}
                    fullWidth
                  />
                  <TextField
                    label="Short Text Content"
                    value={ps.short_text_content}
                    onChange={e => setFormData({ params_secondary: { ...ps, short_text_content: e.target.value } })}
                    fullWidth
                    multiline
                    rows={3}
                  />
                  <TextField
                    label="QR Code Caption"
                    value={ps.image_text}
                    onChange={e => setFormData({ params_secondary: { ...ps, image_text: e.target.value } })}
                    fullWidth
                    helperText="Text displayed below the QR code"
                  />
                  <ImageUploadField
                    label="QR Code image filename"
                    value={ps.image[0] ?? ''}
                    onChange={filename => setFormData({ params_secondary: { ...ps, image: [filename] } })}
                    museumCode={museumCode}
                  />
                </Box>
              );
            })()}

          {/* ── feedback ── */}
          {sectionKey === 'feedback' &&
            (() => {
              const d = formData as ConciergeData['feedback'];
              const questions = d.params_primary;
              const ps = d.params_secondary;

              const updateQuestion = (index: number, field: string, value: string | string[]) => {
                const updated = questions.map((q, i) => (i === index ? { ...q, [field]: value } : q));
                setFormData({ ...d, params_primary: updated });
              };

              const addQuestion = () => {
                setFormData({
                  ...d,
                  params_primary: [...questions, { title: '', question: '', answers: [], type: 'button' }],
                });
              };

              const removeQuestion = (index: number) => {
                setFormData({ ...d, params_primary: questions.filter((_, i) => i !== index) });
              };

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Questions (Primary Screen)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {questions.map((q, qi) => (
                        <Card key={qi} variant="outlined">
                          <CardContent>
                            <Box
                              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}
                            >
                              <Typography variant="subtitle2">Question {qi + 1}</Typography>
                              <Tooltip title="Remove question">
                                <IconButton size="small" color="error" onClick={() => removeQuestion(qi)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <TextField
                                label="Title"
                                value={q.title}
                                onChange={e => updateQuestion(qi, 'title', e.target.value)}
                                fullWidth
                                size="small"
                              />
                              <TextField
                                label="Question / Statement"
                                value={q.question}
                                onChange={e => updateQuestion(qi, 'question', e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                              />
                              <TextField
                                label="Type"
                                value={q.type}
                                onChange={e => updateQuestion(qi, 'type', e.target.value)}
                                size="small"
                                helperText='e.g. "rating" or "button"'
                                sx={{ width: 160 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Answer Options
                              </Typography>
                              {q.answers.map((ans, ai) => (
                                <Box key={ai} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <TextField
                                    label={`Option ${ai + 1}`}
                                    value={ans}
                                    onChange={e => {
                                      const updated = q.answers.map((a, j) => (j === ai ? e.target.value : a));
                                      updateQuestion(qi, 'answers', updated);
                                    }}
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                  />
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      updateQuestion(
                                        qi,
                                        'answers',
                                        q.answers.filter((_, j) => j !== ai),
                                      )
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ))}
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => updateQuestion(qi, 'answers', [...q.answers, ''])}
                                sx={{ alignSelf: 'flex-start' }}
                              >
                                Add Option
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        size="small"
                        onClick={addQuestion}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Add Question
                      </Button>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Feedback QR Code (Secondary Screen)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Title"
                        value={ps.title}
                        onChange={e => setFormData({ ...d, params_secondary: { ...ps, title: e.target.value } })}
                        fullWidth
                      />
                      <TextField
                        label="Short Text Content"
                        value={ps.short_text_content}
                        onChange={e =>
                          setFormData({ ...d, params_secondary: { ...ps, short_text_content: e.target.value } })
                        }
                        fullWidth
                        multiline
                        rows={3}
                      />
                      <TextField
                        label="QR Code Caption"
                        value={ps.image_text}
                        onChange={e => setFormData({ ...d, params_secondary: { ...ps, image_text: e.target.value } })}
                        fullWidth
                      />
                      <ImageUploadField
                        label="QR Code image filename"
                        value={ps.image[0] ?? ''}
                        onChange={filename => setFormData({ ...d, params_secondary: { ...ps, image: [filename] } })}
                        museumCode={museumCode}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })()}

          {/* Save button */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={updateSection.loading}>
              {updateSection.loading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outlined" onClick={() => navigate(backUrl)}>
              Cancel
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarError ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConciergeEditPage;
